import { useEffect, useRef, useState } from "react";

import { reverse } from "@reactivated";
import type { ServerCmsTypesProductSummary as ProductSummary } from "@reactivated";

import type { Favorite } from "../../favorites";

interface UseFavoriteProductsResult {
    products: ProductSummary[] | null;
    isLoading: boolean;
    hasError: boolean;
}

/**
 * Fetches full product card data for the given favorites via the resolve API.
 * Returns null for products while loading; an empty array if nothing resolved.
 */
export const useFavoriteProducts = (
    items: Favorite[],
    locale: string,
): UseFavoriteProductsResult => {
    const [products, setProducts] = useState<ProductSummary[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const productsRef = useRef<ProductSummary[] | null>(null);

    useEffect(() => {
        productsRef.current = products;
    }, [products]);

    useEffect(() => {
        if (items.length === 0) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch effect: empty-items fast path resolves state directly
            setProducts([]);
            setIsLoading(false);
            setHasError(false);
            return;
        }

        // If every current favorite is already in the last resolved set,
        // filter locally instead of refetching. Avoids the skeleton flash
        // (and a wasted network round-trip) when the user removes a favorite
        // from the favorites page.
        const resolved = productsRef.current;
        if (
            resolved &&
            items.every((item) => resolved.some((p) => p.sku === item.sku))
        ) {
            const filtered = resolved.filter((p) =>
                items.some((i) => i.sku === p.sku),
            );
            if (filtered.length !== resolved.length) {
                setProducts(filtered);
            }
            setIsLoading(false);
            setHasError(false);
            return;
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setHasError(false);

        const url = reverse("cms-api:products");
        const params = new URLSearchParams(
            items.map((item) => ["sku", item.sku]),
        );
        params.set("locale", locale);

        fetch(`${url}?${params.toString()}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                return response.json();
            })
            .then((data: { products: ProductSummary[] }) => {
                setProducts(data.products);
            })
            /* v8 ignore start -- @preserve */
            .catch((error) => {
                if (
                    error instanceof DOMException &&
                    error.name === "AbortError"
                ) {
                    return;
                }
                console.error("Failed to resolve favorite products:", error);
                setProducts([]);
                setHasError(true);
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });
        /* v8 ignore stop -- @preserve */

        return () => {
            controller.abort();
        };
    }, [items, locale]);

    return { products, isLoading, hasError };
};
